import React from 'react';
import {
  CartesianGrid,
  Dot,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BucketPoint } from '../../../lib/metricsUtils';
import styles from './DetailMetricsChart.module.sass';

type DetailMetricsChartVariant = 'success' | 'error';

interface DetailMetricsChartProps {
  data: BucketPoint[];
  granularity?: 'daily' | 'hourly';
  variant?: DetailMetricsChartVariant;
  loading?: boolean;
}

const VARIANT_COLORS: Record<DetailMetricsChartVariant, {
  line: string;
  dotFill: string;
  activeDotFill: string;
}> = {
  success: {
    line: styles.successLine,
    dotFill: styles.successDotFill,
    activeDotFill: styles.successActiveDotFill,
  },
  error: {
    line: styles.errorLine,
    dotFill: styles.errorDotFill,
    activeDotFill: styles.errorActiveDotFill,
  },
};

const formatCompact = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return `${Math.round(value)}`;
};

// Powers of 10 from 0 up to the smallest decade >= maxValue. These are the
// human-readable tick *labels*; actual axis positions come from log10(label + 1),
// so 0 lands at 0, 1 at ~0.30, 10 at ~1.04, etc.
const buildLogTickLabels = (maxValue: number): number[] => {
  const labels: number[] = [0, 1];
  let next = 10;
  while (next < Math.max(maxValue, 1)) {
    labels.push(next);
    next *= 10;
  }
  labels.push(next);
  return labels;
};

// log10(x + 1) — the classic "log1p" transform. Compresses large values while
// keeping zero plottable at the baseline (log10(0 + 1) = 0).
const toLog = (value: number): number => Math.log10(Math.max(value, 0) + 1);
const fromLog = (scaled: number): number => Math.round(10 ** scaled - 1);

const DetailMetricsChart: React.FC<DetailMetricsChartProps> = ({
  data,
  granularity = 'daily',
  variant = 'success',
  loading = false,
}) => {
  const colors = VARIANT_COLORS[variant];
  const safe = data.length > 0
    ? data
    : [0, 1, 2, 3, 4, 5].map((index) => ({
      bucket: new Date(Date.now() - ((5 - index) * 24 * 60 * 60 * 1000)).toISOString(),
      value: 0,
    }));

  const tickFormatter = (bucket: string): string => {
    const date = new Date(bucket);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    if (granularity === 'hourly') {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        hour12: true,
        timeZone: 'UTC',
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const maxValue = Math.max(...safe.map((point) => point.value), 0);
  // Plot the log1p-scaled value on a linear axis and translate the tick labels
  // back to original units. Chart starts at 0, 0 values plot at the baseline.
  const logTickLabels = buildLogTickLabels(maxValue);
  const logTopLabel = logTickLabels[logTickLabels.length - 1];
  const yDomain: [number, number] = [0, toLog(logTopLabel)];
  const yTicks = logTickLabels.map(toLog);
  const chartData = safe.map((point) => ({ ...point, scaledValue: toLog(point.value) }));

  return (
    <div
      style={{ width: '100%', height: 235, position: 'relative' }}
      aria-busy={loading ? true : undefined}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 12, right: 6, bottom: 14, left: 10 }}>
          <CartesianGrid
            stroke={styles.grid}
            vertical={false}
            strokeDasharray="2 3"
            horizontalValues={yTicks}
          />
          <XAxis
            dataKey="bucket"
            tickLine={false}
            axisLine={false}
            tick={{ fill: styles.tickText, fontSize: 12 }}
            dy={8}
            minTickGap={granularity === 'hourly' ? 32 : 20}
            tickFormatter={tickFormatter}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: styles.tickText, fontSize: 12 }}
            scale="linear"
            domain={yDomain}
            ticks={yTicks}
            tickFormatter={(value: number) => formatCompact(fromLog(value))}
            width={48}
          />
          <Tooltip
            formatter={(_value: number, _name, props: { payload?: { value?: number } }) => {
              const raw = props.payload?.value ?? 0;
              return [formatCompact(raw), 'Items'];
            }}
            labelFormatter={(bucket: string) => {
              const date = new Date(bucket);
              if (Number.isNaN(date.getTime())) {
                return bucket;
              }

              if (granularity === 'daily') {
                return `${date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone: 'UTC',
                })} UTC`;
              }

              return `${date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'UTC',
              })} UTC`;
            }}
            contentStyle={{
              border: `1px solid ${styles.tooltipBorder}`,
              borderRadius: 8,
              boxShadow: `0 4px 14px ${styles.tooltipShadow}`,
              fontSize: 12,
            }}
            labelStyle={{ color: styles.tooltipLabel }}
          />
          <Line
            type="monotone"
            dataKey="scaledValue"
            stroke={colors.line}
            strokeWidth={3}
            dot={<Dot r={3} fill={colors.dotFill} stroke={styles.dotStroke} strokeWidth={2} />}
            activeDot={{ r: 5, fill: colors.activeDotFill }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      {loading && <div className={styles.skeletonOverlay} aria-hidden={true} />}
    </div>
  );
};

export default DetailMetricsChart;
